import { useEffect, useState } from 'react';
import {
    Button,
    Form,
    Input,
    Upload,
    Select,
    Space,
    Card,
    message,
} from 'antd';
import {
    UploadOutlined,
    PlusOutlined,
    DeleteOutlined,
} from '@ant-design/icons';
import * as LucideIcons from 'lucide-react';
import AboutApi from '../api/about';
import LangApi from '../api/langs';

const iconList = ['Award', 'Star', 'Crown', 'ShieldCheck', 'Trophy'];

const About = () => {
    const [form] = Form.useForm();
    const [langs, setLangs] = useState([]);
    const [selectedLang, setSelectedLang] = useState('ru');
    const [coverFile, setCoverFile] = useState([]);
    const [loading, setLoading] = useState(false);
    const BASE_URL = process.env.REACT_APP_URL;

    const fetchLangs = async () => {
        const res = await LangApi.getAll();
        setLangs(res.data);
    };

    const fetchData = async () => {
        setLoading(true);
        try {
            const res = await AboutApi.getByLocale(selectedLang);
            const data = res.data;

            const lang = langs.find((l) => l.code === selectedLang);
            if (lang) {
                data.lang_id = lang.id;
            }

            form.setFieldsValue(data);
            if (data.cover_image) {
                setCoverFile([
                    {
                        uid: '-1',
                        name: 'cover.jpg',
                        status: 'done',
                        url: `${BASE_URL}${data.cover_image}`,
                    },
                ]);
            } else {
                setCoverFile([]);
            }
        } catch {
            message.error('Ошибка при загрузке');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchLangs();
    }, []);

    useEffect(() => {
        if (langs.length > 0) {
            fetchData();
        }
    }, [selectedLang, langs]);

    const handleSubmit = async (values) => {
        try {
            const formData = new FormData();

            formData.append('lang_id', values.lang_id);
            formData.append('title', values.title);
            formData.append('description', values.description);
            formData.append('image_text', JSON.stringify(values.image_text || {}));
            formData.append('goals', JSON.stringify(values.goals || []));
            formData.append('awards', JSON.stringify(values.awards || []));

            const file = coverFile[0]?.originFileObj;
            if (file) {
                formData.append('cover_image', file);
            }

            await AboutApi.update(selectedLang, formData);

            message.success('Сохранено');
            fetchData();
        } catch {
            message.error('Ошибка при сохранении');
        }
    };

    return (
        <div>
            <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold">О нас</h2>
                <Select
                    value={selectedLang}
                    onChange={setSelectedLang}
                    options={langs.map((l) => ({ label: l.name, value: l.code }))}
                    style={{ width: 160 }}
                />
            </div>

            <Form
                form={form}
                layout="vertical"
                onFinish={handleSubmit}
                initialValues={{ goals: [], awards: [], image_text: {} }}
            >
                <Form.Item name="lang_id" hidden>
                    <Input />
                </Form.Item>

                <Form.Item label="Обложка">
                    <Upload
                        listType="picture"
                        fileList={coverFile}
                        onChange={({ fileList }) => setCoverFile(fileList)}
                        beforeUpload={() => false}
                        accept="image/*"
                        maxCount={1}
                    >
                        <Button icon={<UploadOutlined />}>Загрузить обложку</Button>
                    </Upload>
                </Form.Item>

                <Form.Item
                    name="title"
                    label="Заголовок"
                    rules={[{ required: true }]}
                >
                    <Input />
                </Form.Item>

                <Form.Item
                    name="description"
                    label="Описание"
                    rules={[{ required: true }]}
                >
                    <Input.TextArea rows={4} />
                </Form.Item>

                <Card title="Тексты рядом с изображением" className="mb-4">
                    <Form.Item name={['image_text', 'text1']} label="Текст 1">
                        <Input />
                    </Form.Item>
                    <Form.Item name={['image_text', 'text2']} label="Текст 2">
                        <Input />
                    </Form.Item>
                </Card>

                <Card title="Цели" className="mb-4">
                    <Form.List name="goals">
                        {(fields, { add, remove }) => (
                            <>
                                {fields.map(({ key, name }) => (
                                    <Space key={key} className="block mb-2">
                                        <Form.Item name={name} noStyle>
                                            <Input placeholder="Цель" style={{ width: 400 }} />
                                        </Form.Item>
                                        <Button
                                            icon={<DeleteOutlined />}
                                            onClick={() => remove(name)}
                                            danger
                                        />
                                    </Space>
                                ))}
                                <Button onClick={() => add()} icon={<PlusOutlined />}>
                                    Добавить цель
                                </Button>
                            </>
                        )}
                    </Form.List>
                </Card>

                <Card title="Награды" className="mb-4">
                    <Form.List name="awards">
                        {(fields, { add, remove }) => (
                            <>
                                {fields.map(({ key, name }) => (
                                    <Space
                                        key={key}
                                        direction="vertical"
                                        className="block mb-4 p-4 border rounded"
                                    >
                                        <Form.Item name={[name, 'icon']} label="Иконка">
                                            <Input placeholder="Например: Star, Award" />
                                        </Form.Item>

                                        <Form.Item label="Выбери иконку">
                                            <div className="flex flex-wrap gap-2">
                                                {iconList.map((iconName) => {
                                                    const Icon = LucideIcons[iconName];
                                                    return (
                                                        <Button
                                                            key={iconName}
                                                            onClick={() => {
                                                                const awards = form.getFieldValue('awards') || [];
                                                                awards[name] = {
                                                                    ...(awards[name] || {}),
                                                                    icon: iconName,
                                                                };
                                                                form.setFieldsValue({ awards });
                                                            }}
                                                            icon={<Icon className="w-4 h-4" />}
                                                            size="small"
                                                        >
                                                            {iconName}
                                                        </Button>
                                                    );
                                                })}
                                            </div>
                                        </Form.Item>

                                        <Form.Item name={[name, 'title']} label="Заголовок">
                                            <Input />
                                        </Form.Item>
                                        <Form.Item name={[name, 'description']} label="Описание">
                                            <Input.TextArea rows={2} />
                                        </Form.Item>
                                        <Button
                                            icon={<DeleteOutlined />}
                                            onClick={() => remove(name)}
                                            danger
                                        >
                                            Удалить награду
                                        </Button>
                                    </Space>
                                ))}
                                <Button onClick={() => add()} icon={<PlusOutlined />}>
                                    Добавить награду
                                </Button>
                            </>
                        )}
                    </Form.List>
                </Card>

                <Button type="primary" htmlType="submit">
                    Сохранить
                </Button>
            </Form>
        </div>
    );
};

export default About;
